import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { GoogleMap, Marker } from '@react-google-maps/api';
import LocationPin from '@/assets/svg/location.svg?react';
import { useCallback, useEffect, useState } from 'react';
import CustomInput from '../Input';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 27.7172,
  lng: 85.324,
};

interface AddressFieldWithMapProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  isGoogleMapsLoaded: boolean;
}

export const AddressFieldWithMap = ({
  name,
  label = '',
  isRequired = false,
  isGoogleMapsLoaded,
}: AddressFieldWithMapProps) => {
  const { control, setValue, getValues } = useFormContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentValue, setCurrentValue] = useState(getValues(name) || '');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [address, setAddress] = useState('');
  const [mapError, setMapError] = useState<string | null>(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  // Geocode the existing address to get coordinates
  const geocodeAddress = useCallback(
    (address: string) => {
      if (!isGoogleMapsLoaded || !address) return;

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          const newLocation = { lat: location.lat(), lng: location.lng() };
          setSelectedLocation(newLocation);
          setMapCenter(newLocation);
          setAddress(results[0].formatted_address);
          setIsFetchingLocation(false);
        } else {
          console.error('Geocoding failed:', status);
          setMapError('Unable to find the address. Please select manually.');
          setSelectedLocation(defaultCenter);
          setMapCenter(defaultCenter);
          setAddress('');
          setIsFetchingLocation(false);
        }
      });
    },
    [isGoogleMapsLoaded],
  );

  useEffect(() => {
    if (isOpen && isGoogleMapsLoaded) {
      setIsFetchingLocation(true);

      // Check if there's an existing address in the input field
      const existingAddress = getValues(name);
      if (existingAddress) {
        geocodeAddress(existingAddress);
      } else {
        // Fallback to user's current location if no address is provided
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              const newLocation = { lat: latitude, lng: longitude };
              setSelectedLocation(newLocation);
              setMapCenter(newLocation);
              setIsFetchingLocation(false);

              const geocoder = new google.maps.Geocoder();
              geocoder.geocode({ location: newLocation }, (results, status) => {
                if (status === 'OK' && results?.[0]) {
                  setAddress(results[0].formatted_address);
                } else {
                  setAddress('Address not found');
                }
              });
            },
            (error) => {
              console.error('Error getting location:', error);
              setMapError('Geolocation is not supported or permission denied.');
              setSelectedLocation(defaultCenter);
              setMapCenter(defaultCenter);
              setIsFetchingLocation(false);
            },
          );
        } else {
          setMapError('Geolocation is not supported by this browser.');
          setSelectedLocation(defaultCenter);
          setMapCenter(defaultCenter);
          setIsFetchingLocation(false);
        }
      }
    }
  }, [isOpen, isGoogleMapsLoaded, getValues, name, geocodeAddress]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newLocation = { lat, lng };

      setSelectedLocation(newLocation);
      setMapCenter(newLocation);

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: newLocation }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          setAddress(results[0].formatted_address);
        } else {
          setAddress('Address not found');
        }
      });
    },
    [isGoogleMapsLoaded],
  );

  const handleConfirm = useCallback(() => {
    if (address) {
      setValue(name, address, { shouldValidate: true });
      setCurrentValue(address);
      onClose();
    }
  }, [address, name, onClose, setValue]);

  return (
    <FormControl>
      {label !== '' && (
        <FormLabel fontSize="sm" color="text.secondary" display="inline-flex" alignItems="center">
          {label}
          {isRequired && (
            <Text as="span" color="brand.primary.DEFAULT" ml={1}>
              *
            </Text>
          )}
        </FormLabel>
      )}
      <InputGroup>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <CustomInput
                {...field}
                value={currentValue}
                onChange={(e) => {
                  setCurrentValue(e.target.value);
                  field.onChange(e);
                }}
                id={name}
                fontSize={'md'}
                placeholder={`Enter or select ${label.toLowerCase()}`}
                error={error}
              />
              <InputRightElement>
                <Button onClick={onOpen} variant="ghost" color="brand.primary.DEFAULT" size="sm">
                  <Icon as={LocationPin} boxSize="20px" />
                </Button>
              </InputRightElement>
            </>
          )}
        />
      </InputGroup>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Location</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isGoogleMapsLoaded ? (
              <>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={12}
                  onClick={handleMapClick}
                >
                  {selectedLocation && <Marker position={selectedLocation} />}
                </GoogleMap>

                {isFetchingLocation && (
                  <Text color="text.secondary" mt={2}>
                    Fetching location...
                  </Text>
                )}

                {mapError && (
                  <Text color="red.500" mt={2}>
                    {mapError}
                  </Text>
                )}

                {address && (
                  <Box mt={4}>
                    <strong>Selected Address:</strong> {address}
                  </Box>
                )}
              </>
            ) : (
              <Text color="red.500">
                Google Maps API failed to load. Please check your API key or network connection.
              </Text>
            )}
            <HStack mt={4} justify="flex-end">
              <Button onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                bg="brand.primary.DEFAULT"
                color="white"
                isDisabled={!address || isFetchingLocation}
              >
                Confirm
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </FormControl>
  );
};
